import { useEffect, useState } from "react";
import { Copy, Loader2, QrCode } from "lucide-react";
import OrderUpiQr from "@/components/OrderUpiQr";
import { fetchPartnerInviteInfo } from "@/lib/driverPartner";

const PINK = "#EE2677";

/**
 * Partner join QR — show on screen so the OTHER person scans with their phone camera.
 * Opens the Delivery app sign-up link. No in-app camera.
 */
export default function DriverPartnerQrPanel({ onJoin, onBack, title, hint, variant = "full" }) {
  const [inviteUrl, setInviteUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPartnerInviteInfo()
      .then((d) => {
        if (!cancelled) setInviteUrl(d.invite_url || "");
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copyLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const isInline = variant === "inline";

  return (
    <div className={`da-qr-panel${isInline ? " da-qr-panel--inline" : ""}`}>
      <style>{`
        .da-qr-panel {
          position: fixed;
          inset: 0;
          z-index: 40;
          background: #000;
          color: #fff;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
          display: flex;
          flex-direction: column;
        }
        .da-qr-panel--inline {
          position: relative;
          inset: auto;
          z-index: auto;
          background: #fff;
          color: #2D2D2D;
          margin-bottom: 16px;
          border-radius: 20px;
          padding: 16px;
          border: 2px solid #F9A8D4;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .da-qr-panel * { box-sizing: border-box; }
        .da-qr-panel-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: max(12px, env(safe-area-inset-top)) 16px 16px;
          min-height: 0;
        }
        .da-qr-panel-title {
          font-size: 20px;
          font-weight: 900;
          text-align: center;
          margin-bottom: 8px;
          line-height: 1.25;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .da-qr-panel-subtitle {
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          color: rgba(255,255,255,0.75);
          margin-bottom: 18px;
          line-height: 1.45;
          max-width: 320px;
        }
        .da-qr-panel--inline .da-qr-panel-subtitle {
          color: rgba(45,45,45,0.65);
        }
        .da-qr-panel-frame {
          position: relative;
          width: min(85vw, 360px);
          aspect-ratio: 1;
          background: #fff;
          border-radius: 24px;
          border: 4px solid ${PINK};
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 22px;
          box-shadow: 0 0 0 9999px rgba(0,0,0,0.5);
        }
        .da-qr-panel--inline .da-qr-panel-frame {
          box-shadow: none;
          width: 100%;
          max-width: 260px;
        }
        .da-qr-panel-frame img {
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          border: none !important;
          border-radius: 8px !important;
        }
        .da-qr-panel-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          color: #666;
          font-weight: 700;
        }
        .da-qr-panel-foot {
          padding: 12px 18px max(20px, env(safe-area-inset-bottom));
          background: linear-gradient(to top, rgba(0,0,0,0.92), rgba(0,0,0,0.55));
          text-align: center;
        }
        .da-qr-panel--inline .da-qr-panel-foot {
          background: transparent;
          padding: 12px 0 0;
        }
        .da-qr-panel-hint {
          font-size: 14px;
          font-weight: 700;
          color: rgba(255,255,255,0.88);
          line-height: 1.5;
          margin-bottom: 12px;
        }
        .da-qr-panel--inline .da-qr-panel-hint {
          color: rgba(45,45,45,0.7);
          font-size: 13px;
        }
        .da-qr-panel-link {
          font-size: 11px;
          word-break: break-all;
          color: rgba(255,255,255,0.55);
          margin-bottom: 12px;
          padding: 0 8px;
        }
        .da-qr-panel--inline .da-qr-panel-link {
          color: rgba(45,45,45,0.5);
        }
        .da-qr-panel-join {
          width: 100%;
          max-width: 360px;
          margin: 0 auto 10px;
          border: none;
          border-radius: 16px;
          padding: 16px;
          font-size: 17px;
          font-weight: 800;
          color: #fff;
          background: ${PINK};
          cursor: pointer;
        }
        .da-qr-panel-copy {
          width: 100%;
          max-width: 360px;
          margin: 0 auto 10px;
          border: 2px solid rgba(255,255,255,0.3);
          background: rgba(255,255,255,0.1);
          border-radius: 14px;
          padding: 13px;
          font-size: 15px;
          font-weight: 700;
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .da-qr-panel--inline .da-qr-panel-copy {
          border-color: #E5E7EB;
          background: #F9FAFB;
          color: #374151;
        }
        .da-qr-panel-back {
          width: 100%;
          max-width: 360px;
          margin: 0 auto;
          border: 2px solid rgba(255,255,255,0.3);
          background: transparent;
          border-radius: 14px;
          padding: 13px;
          font-size: 15px;
          font-weight: 700;
          color: #fff;
          cursor: pointer;
        }
        .da-qr-panel-bonus {
          margin-top: 14px;
          font-size: 15px;
          font-weight: 800;
          color: #FDE68A;
        }
        .da-qr-panel--inline .da-qr-panel-bonus {
          color: ${PINK};
          font-size: 13px;
        }
      `}</style>

      <div className="da-qr-panel-main">
        <div className="da-qr-panel-title">
          <QrCode size={24} color={isInline ? PINK : "#fff"} />
          {title || "Delivery partner QR"}
        </div>
        <p className="da-qr-panel-subtitle">
          {hint ||
            "Show this QR. New partner scans with their phone camera → Delivery app opens → they sign up."}
        </p>
        <div className="da-qr-panel-frame">
          {loading ? (
            <div className="da-qr-panel-loading">
              <Loader2 size={36} className="animate-spin" color={PINK} />
              Loading QR…
            </div>
          ) : inviteUrl ? (
            <OrderUpiQr upiUrl={inviteUrl} size={280} alt="Scan to join Table Top deliveries" />
          ) : (
            <div className="da-qr-panel-loading">Could not load QR</div>
          )}
        </div>
      </div>

      <div className="da-qr-panel-foot">
        {!isInline ? (
          <p className="da-qr-panel-hint">
            <strong>New delivery boy?</strong> Scan this QR with your phone camera, then tap the button below to sign up.
          </p>
        ) : null}
        {inviteUrl ? <p className="da-qr-panel-link">{inviteUrl}</p> : null}
        {inviteUrl ? (
          <button type="button" className="da-qr-panel-copy" onClick={copyLink}>
            <Copy size={16} />
            {copied ? "Link copied!" : "Copy join link"}
          </button>
        ) : null}
        {onJoin && !isInline ? (
          <button type="button" className="da-qr-panel-join" onClick={onJoin}>
            I scanned — sign up now
          </button>
        ) : null}
        {onBack ? (
          <button type="button" className="da-qr-panel-back" onClick={onBack}>
            Back to deliveries
          </button>
        ) : null}
      </div>
    </div>
  );
}
